import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import { i18n } from "@/i18n";
import { userAPI } from "@/api";
import getDiscProfileDescriptions from "@/helpers/getDiscProfileDescriptions";
import { PersonalProfileUpdatedEvent, WorkStyleAssessmentCompletedEvent } from "@/telemetry";
import { initWebSocketListeners } from "./userApiOrchestrator";

initWebSocketListeners();

const _state = {
	userProfile: {},
	hasCalendarPermsError: false,
	isAssessmentDialogOpen: false,
	isSavingUserProfile: false,
	isSavingAssessmentResults: false,
	isRequestingCalendarPerms: false,
	isDirty: false,
};

const _getters = {
	discWorkStyleDescriptions: state => {
		const assessment = state.userProfile?.assessments?.DISC;
		if ( !assessment ) {
			return [];
		}
		return getDiscProfileDescriptions( { assessment, person: "you", } );
	},
	hasEnabledCalendarIntegration: state => state.userProfile?.enabledCalendarIntegration,
	hasCompletedCalendarIntegration: state => state.userProfile?.completedCalendarIntegration,
	isNeedingTokenRefresh: state => !!state.userProfile?.renewRequired,
	hasWorkStyle: state => !!state.userProfile.assessments?.DISC,
	hasCalendarPermsError: state => state.hasCalendarPermsError,
	isAssessmentDialogOpen: state => state.isAssessmentDialogOpen,
	isSavingAssessmentResults: state => state.isSavingAssessmentResults,
	isSavingUserProfile: state => state.isSavingUserProfile,
	isRequestingCalendarPerms: state => state.isRequestingCalendarPerms,
	personId: state => state.userProfile.personId,
	userProfile: state => state.userProfile,
	shouldRedirectToOnboarding3: state => {
		const { version, isComplete, } = ( state?.userProfile?.onboarding || {} );
		// eslint-disable-next-line no-magic-numbers
		return version === 3 &&
			isComplete === false;
	},
};

/**
 * Reactions are really just mutations that are called when an
 *   action invokes the store's `commit()` function. Separating them
 *   here for clarity. They are merged into mutations below.
 */
const reactions = {
	userProfileLoaded: ( state, userProfile ) => {
		state.userProfile = {
			...userProfile,
		};
	},
	userProfileSaved: ( state, userProfile ) => {
		state.userProfile = {
			...userProfile,
		};
	},
	setHasCalendarPermsError: ( state, hasCalendarPermsError ) => {
		state.hasCalendarPermsError = hasCalendarPermsError;
	},
	setIsRequestingCalendarPerms: ( state, isRequestingCalendarPerms ) => {
		state.isRequestingCalendarPerms = isRequestingCalendarPerms;
	},
};

const mutations = {
	closeAssessmentDialog: state => {
		state.isAssessmentDialogOpen = false;
	},
	openAssessmentDialog: state => {
		state.isAssessmentDialogOpen = true;
	},
	toggleAssessmentDialog: ( state, isOpen ) => {
		state.isAssessmentDialogOpen = !!isOpen;
	},
	toggleSavingAssessmentResults: ( state, isSavingAssessmentResults ) => {
		state.isSavingAssessmentResults = isSavingAssessmentResults;
	},
	toggleSavingUserProfile: ( state, isSavingUserProfile ) => {
		state.isSavingUserProfile = isSavingUserProfile;
	},
	userAssessmentSaved: state => {
		state.isAssessmentDialogOpen = false;
	},
	...reactions,
};

const actions = {
	reloadUserProfile: async ( { commit, } ) => {
		const userProfile = await userAPI.getUserProfile();
		commit( "userProfileLoaded", userProfile );
	},
	saveUserProfile: async ( { commit, }, userProfile ) => {
		commit( "toggleSavingUserProfile", true );

		let wasSuccessful = true;
		try {
			Nprogress.start();
			const savedUser = await userAPI.saveUserProfile( { userProfile, } );
			const event = new PersonalProfileUpdatedEvent();
			event.submit();
			commit( "userProfileSaved", savedUser );
			VueNotifications.success( {
				message: i18n.t( "profile.PersonalInfo_saveUserProfileSuccessMessage" ),
			} );
		} catch ( e ) {
			wasSuccessful = false;
			VueNotifications.error( {
				message: i18n.t( "profile.PersonalInfo_saveUserProfileErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
		commit( "toggleSavingUserProfile", false );
		return wasSuccessful;
	},
	saveDiscAssessmentResponse: async ( { commit, getters: { hasWorkStyle, }, }, assessment ) => {
		// do not attempt to save this multiple times
		if ( hasWorkStyle ) {
			return;
		}
		commit( "toggleSavingAssessmentResults", true );

		let wasSuccessful = true;
		try {
			Nprogress.start();
			const savedAssessment = await userAPI.saveAssessmentResult( { assessment, } );
			const event = new WorkStyleAssessmentCompletedEvent();
			event.submit();
			const userProfile = await userAPI.getUserProfile();
			commit( "userProfileLoaded", userProfile );
			commit( "userAssessmentSaved", savedAssessment );
			VueNotifications.success( {
				message: i18n.t( "profile.AssessmentDialog_saveUserAssessmentSuccessMessage" ),
			} );
		} catch ( e ) {
			wasSuccessful = false;
			VueNotifications.error( {
				message: i18n.t( "profile.AssessmentDialog_saveUserAssessmentErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
		commit( "toggleSavingAssessmentResults", false );
		return wasSuccessful;
	},
	indicateCalendarPermsRequestInProgress: ( { commit, } ) => {
		commit( "setIsRequestingCalendarPerms", true );
		commit( "setHasCalendarPermsError", false );
	},
	indicateCalendarPermsRequestError: ( { commit, }, { email, targetEmail, } ) => {
		commit( "setHasCalendarPermsError", true );
		commit( "setIsRequestingCalendarPerms", false );
		if ( email && targetEmail ) {
			VueNotifications.error( {
				message: i18n.t( "profile.PersonalInfo_calPermsWrongAccountErrorMessage", { email, targetEmail, } ),
			} );
		}
	},
};

export default {
	state: _state,
	getters: _getters,
	mutations,
	actions,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/userProfile/index.js